import {
  ArnoEvidenceStatus,
  IArnoMappingRule,
} from '../../hooks/reactQuerys/ArnoAdminQueries';

/**
 * Zuordnungsregeln für nummerierte Legacy-Familien (#1245).
 *
 * Eine Regel trifft dutzende Felder auf einmal. Deshalb ist hier die wichtigste
 * Frage nicht „was bringt sie", sondern „was fehlt ihr noch" — und die Antwort
 * muss vor der Schaltfläche stehen, nicht dahinter.
 */

export const RULE_STATUS_LABEL: Record<string, string> = {
  UNMAPPED: 'Offen',
  SUGGESTED: 'Vorschlag',
  CONFIRMED: 'Bestätigt',
  IGNORED: 'Bewusst ohne Ziel',
};

export type ChipColor =
  | 'default'
  | 'primary'
  | 'success'
  | 'warning'
  | 'error'
  | 'info';

/** Die Parameter einer Schablone, in der Reihenfolge ihres Auftretens. */
export const patternParameters = (pattern: string): string[] => {
  const found = pattern.match(/\{([a-zA-Z][a-zA-Z0-9]*)\}/g) ?? [];
  const names = found.map(entry => entry.slice(1, -1));
  return names.filter((name, index) => names.indexOf(name) === index);
};

/**
 * Was der Bestätigung noch fehlt — in der Reihenfolge, in der es jemand lösen
 * muss. Eine leere Liste heißt: die Regel ist entscheidbar.
 */
export const missingForConfirmation = (rule: IArnoMappingRule): string[] => {
  const missing: string[] = [];
  if (rule.evidenceStatus !== 'PROVEN') {
    missing.push('Die Bedeutung ist noch nicht belegt.');
  }
  if (!rule.targetPattern) {
    missing.push('Es gibt kein DiNo-Zielfeld.');
  } else {
    const open = patternParameters(rule.sourcePattern).filter(
      name => !rule.indexRule?.[name]
    );
    if (open.length > 0) {
      missing.push(
        `Für ${open.join(', ')} ist nicht gesagt, womit Altbestand und DiNo zählen.`
      );
    }
  }
  if ((rule.impact?.conflictCount ?? 0) > 0) {
    missing.push(`${rule.impact?.conflictCount} Konflikte sind offen.`);
  }
  return missing;
};

export const isConfirmable = (rule: IArnoMappingRule): boolean =>
  missingForConfirmation(rule).length === 0 && rule.status !== 'CONFIRMED';

/** Die Indexregel als Satz — „Altbestand zählt ab 1, DiNo ab 0, also n − 1". */
export const indexSentence = (
  rule: IArnoMappingRule,
  parameter: string
): string => {
  const entry = rule.indexRule?.[parameter];
  if (!entry) {
    return `${parameter}: nicht festgelegt`;
  }
  const shift = entry.dinoBase - entry.legacyBase;
  const transform =
    shift === 0
      ? parameter
      : `${parameter} ${shift > 0 ? '+' : '−'} ${Math.abs(shift)}`;
  return `${parameter}: Altbestand ab ${entry.legacyBase}, DiNo ab ${entry.dinoBase} → ${transform}`;
};

/** Familien mit ihrer gemessenen Hebelwirkung, größte zuerst. */
export interface IFamilySummary {
  family: string;
  rules: number;
  fields: number;
  blockedIfs: number;
  confirmed: number;
  evidence: ArnoEvidenceStatus;
}

export const familySummaries = (
  rules: IArnoMappingRule[]
): IFamilySummary[] => {
  const byFamily = new Map<string, IFamilySummary>();
  rules.forEach(rule => {
    const key = rule.family ?? 'Ohne Familie';
    const entry = byFamily.get(key) ?? {
      family: key,
      rules: 0,
      fields: 0,
      blockedIfs: 0,
      confirmed: 0,
      evidence: rule.evidenceStatus,
    };
    entry.rules += 1;
    entry.fields += rule.impact?.fields ?? 0;
    entry.blockedIfs += rule.impact?.blockedIfs ?? 0;
    entry.confirmed += rule.status === 'CONFIRMED' ? 1 : 0;
    // Die schwächste Beweislage der Familie zählt — eine Familie ist nicht
    // belegt, weil eine ihrer Regeln es ist.
    const order: ArnoEvidenceStatus[] = [
      'PROVEN',
      'VERY_STRONG_EVIDENCE',
      'AMBIGUOUS',
      'UNKNOWN',
    ];
    if (order.indexOf(rule.evidenceStatus) > order.indexOf(entry.evidence)) {
      entry.evidence = rule.evidenceStatus;
    }
    byFamily.set(key, entry);
  });
  return Array.from(byFamily.values()).sort(
    (left, right) => right.blockedIfs - left.blockedIfs
  );
};

/** Ein Satz zur Wirkung einer Regel — Felder, Vorkommen, Bedingungen. */
export const ruleImpactSentence = (rule: IArnoMappingRule): string => {
  const impact = rule.impact;
  if (!impact) {
    return 'Noch nicht gerechnet.';
  }
  return `${impact.fields} Legacy-Felder · ${impact.occurrences} Vorkommen · ${impact.blockedIfs} blockierte Bedingungen`;
};
